/**
 * Unified API Error Handler
 * Provides consistent error handling, sanitization, and user-friendly messaging across all API calls.
 */

export class APIError extends Error {
  constructor(
    public readonly status: number,
    public readonly userMessage: string,
    public readonly internalMessage?: string,
    public readonly originalError?: Error
  ) {
    super(userMessage);
    this.name = 'APIError';
    Object.setPrototypeOf(this, APIError.prototype);
  }
}

/**
 * Maps HTTP status codes to user-friendly messages
 * Never exposes system architecture or implementation details
 */
const STATUS_MESSAGES: Record<number, string> = {
  400: "Invalid request format",
  401: "Authentication required - please log in",
  403: "You don't have permission for this action",
  404: "Resource not found",
  409: "This resource has been modified - please refresh and try again",
  425: "Action on cooldown - please wait a moment before trying again",
  429: "Too many requests - please wait a moment before retrying",
  500: "Server error - our team has been notified",
  502: "Service temporarily unavailable - please try again",
  503: "Service is under maintenance - please try again later",
};

/**
 * Handles API responses and converts them to consistent APIError objects
 * @param response - The fetch Response object
 * @param operationName - Human-readable name for the operation (e.g., "fetch user servers")
 * @throws APIError with sanitized user message and internal logging details
 */
export async function handleAPIResponse<T>(
  response: Response,
  operationName: string
): Promise<T> {
  if (response.ok) {
    return response.json();
  }

  // Try to parse error details from response
  let errorDetails: any = {};
  try {
    errorDetails = await response.json();
  } catch {
    // Response body is not JSON, use empty object
  }

  // Extract safe error message from API response
  const apiMessage = errorDetails?.error || errorDetails?.message || null;

  // Determine user-facing message
  const userMessage =
    STATUS_MESSAGES[response.status] ||
    `${operationName} failed`;

  // Log internal details for debugging (never shown to user)
  const internalMessage = `[${response.status}] ${operationName} failed: ${apiMessage || response.statusText}`;

  console.error(`[API Error] ${internalMessage}`);

  throw new APIError(
    response.status,
    userMessage,
    internalMessage,
    new Error(apiMessage)
  );
}

/**
 * Wraps an async API function with standard error handling
 * @param fn - Async function that performs the API call
 * @param operationName - Human-readable operation name for error messages
 */
export async function withAPIErrorHandling<T>(
  fn: () => Promise<T>,
  operationName: string
): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    if (error instanceof APIError) {
      throw error; // Already handled
    }

    // Handle network errors or parse errors
    const networkMessage = `${operationName} failed due to a network error`;
    console.error(`[API Network Error] ${operationName}:`, error);

    throw new APIError(
      0, // No HTTP status for network errors
      networkMessage,
      error?.message || "Unknown error",
      error
    );
  }
}

/**
 * Validates Discord ID format to prevent injection attacks
 * @param id - The Discord ID to validate (user ID, guild ID, etc.)
 * @returns true if valid Discord ID format
 */
export function isValidDiscordId(id: string | undefined): boolean {
  if (!id || typeof id !== "string") return false;
  // Discord IDs are numeric strings, typically 18-20 digits
  return /^\d{17,20}$/.test(id);
}

/**
 * Validates Discord avatar hash format
 * @param hash - The avatar hash from Discord
 * @returns true if valid Discord avatar hash format
 */
export function isValidAvatarHash(hash: string | undefined): boolean {
  if (!hash || typeof hash !== "string") return false;
  // Discord avatar hashes are alphanumeric with underscores/hyphens, typically 26+ chars
  // Format: base16 or base2048 encoded, no special characters except _ and -
  return /^[a-zA-Z0-9_-]{26,}$/.test(hash);
}
